<template>
  <v-card flat>
    <v-row no-gutters>
      <v-col cols="12">
        <v-btn
          color="primary"
          icon
          @click="handleBack"
          :disabled="
            loadingSkipButton ||
            loadingDeletePauseThumb ||
            loadingPauseThumbnail
          "
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-h6 mr-2" style="vertical-align: middle">
          Thumbnails
        </span>
      </v-col>

      <v-row no-gutters class="py-0 px-2">
        <v-col cols="12">
          <v-card flat>
            <span>
              {{ $t('message.pages.video.thumbnails.subtitle') }}
            </span>
          </v-card>
        </v-col>
      </v-row>

      <v-col cols="12" class="mt-4 mb-0 pb-5">
        <v-row no-gutters class="px-2">
          <span class="font-weight-medium mb-2" style="font-size: 18px">
            {{ $t('message.pages.video.thumbnails.start') }}
          </span>
          <v-col cols="12" class="d-md-inline-flex container__thumb-list">
            <sendThumbCard v-if="hasEditVideoPerm" :loading="loading">
              <v-file-input
                v-model="newThumbnail.file"
                v-if="!loading"
                height="20px"
                class="d-flex align-center justify-center ma-0 pa-0 pl-2"
                hide-input
                prepend-icon="none"
                accept="image/png, image/jpeg, image/gif"
                @change="handleChangeThumbnail"
              ></v-file-input>
            </sendThumbCard>

            <previewThumbCard
              :loadingDelete="loadingDeleteThumb"
              :thumb="defaultThumbnail"
              :showCloseBtn="
                Boolean(
                  !isBunnyThumbnail &&
                    !loadingDeleteThumb &&
                    defaultThumbnail &&
                    hasEditVideoPerm
                )
              "
              @delete="handleDeleteThumbnail"
              @download="handleDownloadThumb(defaultThumbnail)"
              :showDownloadBtn="true"
              :selected="true"
            />
            <template v-if="filteredDefaultThumbsInfo.length">
              <previewThumbCard
                v-for="(thumb, index) in filteredDefaultThumbsInfo"
                :key="`defaultThumb_${index}`"
                :loadingDelete="false"
                :thumb="thumb"
                :showCloseBtn="false"
                :selected="false"
                :showDownloadBtn="true"
                @download="handleDownloadThumb(thumb)"
                @select="
                  () => {
                    handleUpdateDefaultThumb(thumb)
                  }
                "
              />
            </template>
          </v-col>
        </v-row>

        <v-divider class="mx-2 my-8"></v-divider>

        <v-row no-gutters class="px-2">
          <v-row no-gutters :class="{ 'mb-2': showEditPauseThumbs }">
            <v-col>
              <span class="font-weight-medium" style="font-size: 18px">
                {{ $t('message.pages.video.thumbnails.pause') }}
                <learn-more-btn :onClickBtn="handleOpenThumbLink" />
              </span>
            </v-col>
            <v-col class="d-flex justify-end" v-if="!showEditPauseThumbs">
              <BaseBtn
                type="secondary"
                size="xsmall"
                @click="showEditPauseThumbs = true"
              >
                {{ $t('message.pages.video.thumbnails.addThumb') }}</BaseBtn
              >
            </v-col>
          </v-row>

          <template v-if="showEditPauseThumbs">
            <v-row no-gutters v-if="selectedPauseThumb.metadata">
              <v-col cols="12" class="my-1"> {{ $t('message.name') }} *</v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="selectedPauseThumb.metadata.thumb.title"
                  dense
                  outlined
                  hide-details
                  :rules="[pauseThumbValidations.name]"
                  :placeholder="$t('message.pages.video.thumbnails.thumbName')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="mt-4 mb-1">
                <v-row no-gutters>
                  <v-col cols="6" class="pr-2">
                    {{ $t('message.pages.videoPlayerButtons.start') }} ({{
                      timePickerFormat
                    }})
                  </v-col>
                  <v-col cols="6" class="pl-2">
                    {{ $t('message.pages.pauseThumbnail.end') }} ({{
                      timePickerFormat
                    }})
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12">
                <v-row no-gutters>
                  <v-col cols="6" class="pr-2">
                    <!-- start -->
                    <AntTimePicker
                      :key="timePickerRenderKeys.start"
                      :startVal="
                        formatSecondsToTime(selectedPauseThumb.startTime)
                      "
                      :timeVideo="timeVideo"
                      :currentVal="
                        formatSecondsToTime(selectedPauseThumb.startTime)
                      "
                      :disabled="selectedPauseThumb.metadata.thumb.lastAllVideo"
                      @changedTime="handleStartTimeEvent"
                    />
                  </v-col>
                  <v-col cols="6" class="pl-2">
                    <!-- end -->
                    <AntTimePicker
                      :key="timePickerRenderKeys.end"
                      :startVal="
                        isLowerOrEqual
                          ? formatSecondsToTime(selectedPauseThumb.startTime)
                          : formatSecondsToTime(selectedPauseThumb.endTime)
                      "
                      :thresholdTime="
                        formatSecondsToTime(selectedPauseThumb.startTime)
                      "
                      :timeVideo="timeVideo"
                      :currentVal="
                        formatSecondsToTime(selectedPauseThumb.endTime)
                      "
                      :disabled="
                        selectedPauseThumb.metadata.thumb.lastAllVideo ||
                        selectedPauseThumb.metadata.thumb.lastTillEnd
                      "
                      @changedTime="handleEndTimeEvent"
                    />
                  </v-col>
                  <v-col cols="12" class="mt-2">
                    <v-row no-gutters>
                      <v-col cols="6" class="pr-2">
                        <v-checkbox
                          v-model="
                            selectedPauseThumb.metadata.thumb.lastAllVideo
                          "
                          :label="
                            $t('message.pages.videoPlayerButtons.lastAllVideo')
                          "
                          class="pa-0 ma-0"
                          color="primary"
                          hide-details
                          @change="handleLastAllVideo"
                        ></v-checkbox>
                      </v-col>
                      <v-col cols="6" class="pl-2">
                        <v-checkbox
                          v-model="
                            selectedPauseThumb.metadata.thumb.lastTillEnd
                          "
                          :label="
                            $t('message.pages.videoPlayerButtons.lastTillEnd')
                          "
                          dense
                          class="pa-0 ma-0"
                          color="primary"
                          hide-details
                          @change="handleLastTillEnd"
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col v-if="checkIfIsBetweenInterval" cols="12">
                    <span class="red--text caption align-text">
                      <v-icon color="red" size="16" class="pb-1 pt-1 pr-1">
                        mdi-alert-circle
                      </v-icon>
                      {{
                        $t(
                          'message.pages.pauseThumbnail.alreadyExistsInThisTime'
                        )
                      }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-col cols="12" class="d-md-inline-flex container__thumb-list">
              <sendThumbCard v-if="hasEditVideoPerm" :loading="false">
                <v-file-input
                  v-model="newPauseThumbnail.file"
                  height="20px"
                  class="d-flex align-center justify-center ma-0 pa-0 pl-2"
                  hide-input
                  prepend-icon="mdi-cloud-upload-outline"
                  accept="image/png, image/jpeg, image/gif"
                  @change="
                    () => {
                      showPreviewImg()
                    }
                  "
                ></v-file-input>
              </sendThumbCard>

              <previewThumbCard
                v-for="(pauseThumb, i) in pauseThumbnails"
                :key="i"
                :loadingDelete="
                  loadingDeletePauseThumb &&
                  beingDeletePauseThumbId === pauseThumb.id
                "
                :thumb="
                  pauseThumb.metadata.fileUrl || defaultPauseThumbnail || ''
                "
                :showCloseBtn="
                  !Boolean(
                    loadingDeletePauseThumb &&
                      beingDeletePauseThumbId === pauseThumb.id
                  )
                "
                @delete="handleRemoveThumb(pauseThumb, i)"
                @select="
                  () => {
                    selectedPauseThumb = pauseThumb
                    handlePlayerPreviewPauseThumb()
                  }
                "
                @download="handleDownloadThumb(pauseThumb.metadata.fileUrl)"
                :showDownloadBtn="true"
                :selected="selectedPauseThumb.id === pauseThumb.id"
              />
            </v-col>
            <template v-if="selectedPauseThumb.metadata">
              <v-col cols="12" class="mt-1 mb-2">
                <v-row no-gutters align="center">
                  <v-switch
                    v-model="selectedPauseThumb.metadata.thumb.hasButton"
                    hide-details
                    class="ma-0"
                    @change="() => handlePlayerPreviewPauseThumb()"
                  ></v-switch>
                  <span class="pt-1">{{
                    $t('message.pages.videoPlayerButtons.addBtn')
                  }}</span>
                </v-row>
              </v-col>
              <video-player-buttons
                v-if="selectedPauseThumb.metadata.thumb.hasButton"
                :isThumbButton="true"
                :handleBack="handleBack"
              />
            </template>
          </template>
        </v-row>

        <v-row no-gutters class="px-2" v-if="showEditPauseThumbs">
          <v-col class="d-flex justify-end">
            <BaseBtn
              type="tertiary"
              class="mr-2"
              min-width="150"
              :disabled="
                cantUploadPauseThumb || isEmpty(originalSelectedPauseThumb)
              "
              @click="handleDiscardPauseThumb"
            >
              {{ $t('message.pages.video.discardChanges') }}
            </BaseBtn>
            <BaseBtn
              min-width="150"
              :disabled="cantUploadPauseThumb"
              :loading="loadingPauseThumbnail"
              @click="handlePublishPauseThumbnail"
              >{{ $t('message.pages.videoPlayerButtons.publish') }}
            </BaseBtn>
          </v-col>
        </v-row>

        <v-divider class="mx-2 my-8"></v-divider>

        <v-row no-gutters class="px-2">
          <v-row no-gutters :class="{ 'mb-2': showEditPauseThumbs }">
            <v-col>
              <span class="font-weight-medium mb-2" style="font-size: 18px">
                {{ $t('message.pages.video.thumbnails.end') }}
              </span>
            </v-col>
          </v-row>

          <template>
            <v-col cols="12" class="d-md-inline-flex container__thumb-list">
              <sendThumbCard
                v-if="hasEditVideoPerm"
                :loading="loadingEndThumbnail"
              >
                <v-file-input
                  v-if="!loadingEndThumbnail"
                  v-model="newEndThumbnail.file"
                  height="20px"
                  class="d-flex align-center justify-center ma-0 pa-0 pl-2"
                  hide-input
                  prepend-icon="none"
                  accept="image/png, image/jpeg, image/gif"
                  @change="handleUploadEndThumbnail"
                ></v-file-input>
              </sendThumbCard>

              <previewThumbCard
                v-if="defaultEndThumbnail"
                :loadingDelete="loadingDeleteEndThumb"
                :thumb="defaultEndThumbnail"
                :showCloseBtn="!loadingDeleteEndThumb"
                @download="handleDownloadThumb(defaultEndThumbnail)"
                @delete="handleDeleteEndThumbnail('removeButton')"
                :showDownloadBtn="true"
                :selected="true"
              />
            </v-col>
          </template>
        </v-row>

        <v-card flat class="pt-2">
          <v-row
            no-gutters
            v-if="successfulChangedThumb && !errors.updateThumbnailVideo"
          >
            <v-col class="pa-0" style="max-width: min-content">
              <v-icon color="primary">mdi-check</v-icon>
            </v-col>
            <v-col class="pa-0">
              <span
                class="caption align-text"
                v-html="$t('message.pages.videoEditModal.serverUpdateOnUpload')"
              />
            </v-col>
          </v-row>

          <v-row
            no-gutters
            v-else-if="successfulDeleteThumb && !errors.updateThumbnailVideo"
          >
            <v-col class="pa-0" style="max-width: min-content">
              <v-icon color="primary">mdi-check</v-icon>
            </v-col>
            <v-col class="pa-0">
              <span
                class="caption align-text"
                v-html="$t('message.pages.video.serverDeleteThumbnail')"
              />
            </v-col>
          </v-row>

          <v-row
            no-gutters
            v-else-if="
              errors.updateThumbnailVideo &&
              !errors.thumbAboveMaxSize &&
              (editingThumbnail.type === 'start' ||
                editingThumbnail.type === 'end')
            "
          >
            <v-col class="px-2">
              <span
                class="caption align-text"
                style="color: #f44336"
                v-html="$t('message.pages.video.errors.updateThumbnail')"
              />
            </v-col>
          </v-row>

          <v-row
            no-gutters
            v-else-if="
              errors.updateThumbnailVideo &&
              errors.thumbAboveMaxSize &&
              (editingThumbnail.type === 'start' ||
                editingThumbnail.type === 'end')
            "
          >
            <v-col class="px-2">
              <span
                class="caption align-text"
                style="color: #f44336"
                v-html="$t('message.pages.video.errors.aboveMaxSizeThumbnail')"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
/* eslint-disable */

// external imports
import download from 'downloadjs'
import { v4 as uuidv4 } from 'uuid'
import { mapActions, mapMutations, mapState } from 'vuex'
import _ from 'lodash'

// internal imports
import { THUMB_URL } from '@/config/envs.js'
import linkHelper from '@/utils/linkHelper.mixin.js'

// API
import videosApi from '@/services/videosApi'
import playerApi from '@/services/playerApi'

// Components
import VideoPlayerButtons from './VideoPlayerButtons.vue'
import LearnMoreBtn from '@/components/LearnMoreBtn.vue'
import AntTimePicker from '@/components/AntTimePicker.vue'
import sendThumbCard from '@/components/sendThumbCard.vue'
import previewThumbCard from '@/components/previewThumbCard.vue'
import BaseBtn from '@/components/BaseBtn.vue'

export default {
  name: 'VideoPlayerThumbnails',
  components: {
    VideoPlayerButtons,
    LearnMoreBtn,
    AntTimePicker,
    sendThumbCard,
    previewThumbCard,
    BaseBtn,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
  },
  mixins: [linkHelper],
  data: () => ({
    videoId: '',
    newThumbnail: {
      file: null,
    },
    renderTick: 0,
    newPauseThumbnail: {
      file: null,
    },
    newEndThumbnail: {
      file: null,
    },
    showEditPauseThumbs: false,
    loading: false,
    loadingPauseThumbnail: false,
    loadingEndThumbnail: false,
    loadingDeleteThumb: false,
    loadingDeletePauseThumb: false,
    loadingDeleteEndThumb: false,
    successfulChangedThumb: false,
    successfulChangedPauseThumb: false,
    successfulChangedEndThumb: false,
    successfulDeleteThumb: false,
    successfulDeletePauseThumb: false,
    successfulDeleteEndThumb: false,
    errors: {
      addThumbnail: false,
      wrongThumbnailFile: false,
      updateThumbnailVideo: false,
      updateThumbnailPauseVideo: false,
      updateThumbnailEndVideo: false,
      thumbAboveMaxSize: false,
    },
    defaultThumbnail: null,
    defaultPauseThumbnail: null,
    defaultEndThumbnail: null,
    arrayCtas: [],
    havePauseThumb: false,
    haveEndThumb: false,
    activeSkipButton: true,
    loadingSkipButton: false,
    isEditingThumb: false,
    editingThumbnail: {
      title: '',
      startTime: '00:00',
      endTime: '00:05',
      hasButton: false,
      type: 'start',
      lastTillEnd: false,
      lastAllVideo: false,
    },
    selectedPauseThumb: {},
    timeVideo: null,
    scrollInvoked: 0,
    pauseThumbnails: [],
    originalPauseThumbnailsList: [],
    beingEditedPauseThumb: {},
    isThumbUpdate: false,
    beingDeletePauseThumbId: '',
    previewImg: null,
    popupStartTime: true,
    popupEndTime: true,
    defaultThumbsInfo: {},
    pauseThumbValidations: {
      name: (value) => {
        if (value.length) return true
        return false
      },
    },
  }),
  async mounted() {
    this.defaultThumbnail = this.$store.state.video.video?.thumbnail
    this.formatVideoLength()
    this.getVideoDefaultThumbs()

    this.videoId = this.liveVideoId ?? this.$route.params.id
    this.arrayCtas = this.$store.state.video.video.config?.ctas ?? []
    this.defaultThumbnail = this.$store.state.video.video?.thumbnail
    if (this.$store.state.video.video?.endThumbnail) {
      this.defaultEndThumbnail = this.$store.state.video.video?.endThumbnail
    } else {
      const existsEndCta = this.arrayCtas.find((item) => item.type === 'end')
      if (existsEndCta) {
        if (existsEndCta.metadata && existsEndCta.metadata.fileUrl) {
          this.defaultEndThumbnail = existsEndCta.metadata.fileUrl
        } else {
          // get thumb url from img tag src
          try {
            const imgUrl = existsEndCta.script.match(/src='([^']*)'/)[1]
            if (imgUrl) {
              this.defaultEndThumbnail = imgUrl
            }
          } catch (error) {
            console.log('failed to get end thumb from cta')
          }
        }
      }
    }

    let pauseThumbs = this.arrayCtas.filter((item) => item.type === 'pause')
    let mappedPauseThumbs = pauseThumbs.map((item) => {
      if (item.metadata) return item
      else {
        let thumbFilename = this.$store.state.video.video?.config.pauseThumbnail

        return {
          ...item,
          metadata: {
            thumb: {
              ...this.editingThumbnail,
              title: 'Thumb',
              type: 'pause',
              hasButton: false,
            },
            fileUrl: this.$store.state.video.video?.pauseThumbnail,
            thumbId: thumbFilename,
          },
          startTime: 0,
          endTime: this.$store.state.video.video?.length,
        }
      }
    })

    this.pauseThumbnails = mappedPauseThumbs
    this.originalPauseThumbnailsList = _.cloneDeep(this.pauseThumbnails)

    if (!this.pauseThumbnails.length) {
      this.selectedPauseThumb = this.getDefaultPauseThumbObj()
    } else {
      this.showEditPauseThumbs = true
      this.selectedPauseThumb = this.pauseThumbnails[0]
      this.checkIfSelectedPauseThumbHasButton()
      this.handlePlayerPreviewPauseThumb()
    }

    this.$root.$on('buttonsFormInfo', (createdBtnInfo) => {
      this.selectedPauseThumb.metadata.button = createdBtnInfo.buttonMetadata
      this.selectedPauseThumb.metadata.thumb.hasButton = true
      this.handleUploadPauseThumbnail(createdBtnInfo)
    })
  },
  beforeDestroy() {
    this.$root.$off('buttonsFormInfo')
  },
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    filteredDefaultThumbsInfo() {
      if (this.defaultThumbsInfo?.default) {
        return this.defaultThumbsInfo.default.filter((item) => {
          if (item.includes('thumbnail_3.jpg')) return false
          if (item === this.defaultThumbnail) return false
          return true
        })
      }
      return []
    },
    defaultBtn() {
      return {
        title: this.$i18next.t('message.pages.video.buttonDefaultText'),
        link: '',
        startTime: '00:00',
        endTime: '00:05',
        selectedButtonType: 'insideVideo',
        textColor: '#FFFFFF',
        textHover: '#FFFFFF',
        backgroundColor: '#3e50f7',
        backgroundHover: '#3e50f7',
        selectedButtonSize: 'small',
        selectedButtonPlacement: 'bottomCenter',
        isCustomHtml: false,
        codeHtml: '',
        lastAllVideo: false,
        lastTillEnd: false,
        keepWindow: false,
        appendWebsiteQueryParams: false,
      }
    },
    timePickerRenderKeys: {
      get() {
        const ret = {
          start: `start__${this.selectedPauseThumb.id}-${this.renderTick}`,
          end: `end__${this.selectedPauseThumb.id}-${this.renderTick}`,
        }
        return ret
      },
      deep: true,
      cache: false,
    },
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    timePickerFormat() {
      if (this.timeVideo >= 3600) {
        return 'HH:mm:ss'
      }
      return 'mm:ss'
    },
    isBunnyThumbnail() {
      return this.defaultThumbnail
        ? this.defaultThumbnail.includes('thumbnail')
        : false
    },
    originalSelectedPauseThumb() {
      const originalObj = this.originalPauseThumbnailsList.filter(
        (item) => item.id === this.selectedPauseThumb.id
      )
      if (originalObj.length) return { ...originalObj[0] }
      return {}
    },
    cantUploadPauseThumb: {
      get() {
        const selectedPauseThumb = { ...this.selectedPauseThumb }
        if (!selectedPauseThumb.metadata?.fileUrl) {
          return true
        }
        if (
          (this.originalSelectedPauseThumb &&
            _.isEqual(selectedPauseThumb, this.originalSelectedPauseThumb)) ||
          !selectedPauseThumb.metadata.thumb.title
        ) {
          return true
        }
        return this.loadingPauseThumbnail || this.checkIfIsBetweenInterval
      },
      cache: false,
      deep: true,
    },
    checkIfIsBetweenInterval: {
      get() {
        if (
          !this.selectedPauseThumb.startTime &&
          this.selectedPauseThumb.startTime !== 0
        )
          return false
        let hasNewThumbCreated = false
        let x = this.pauseThumbnails.map((el) => {
          if (el.id === this.selectedPauseThumb.id)
            return { start: -1, end: -1 }
          if (!hasNewThumbCreated && (el.startTime || el.endTime)) {
            hasNewThumbCreated = true
          }
          return {
            start: el.startTime,
            end: el.endTime,
          }
        })
        let result = []
        let z = {
          start: this.selectedPauseThumb.startTime,
          end: this.selectedPauseThumb.endTime,
        }
        if (hasNewThumbCreated) {
          result = x.filter(
            (i) =>
              this.inRange(z.start, i.start, i.end) ||
              this.inRange(z.end, i.start, i.end)
          )
        } else {
          result = x.filter(
            (i) =>
              this.inRange(z.start, i.start, i.end) ||
              this.inRange(z.end, i.start, i.end)
          )
        }
        return !!result.length
      },
      deep: true,
      cache: false,
    },
    isLowerOrEqual() {
      const { endTime, startTime } = this.selectedPauseThumb
      return endTime < startTime
    },
  },
  watch: {
    successfulChangedThumb() {
      if (this.successfulChangedThumb) {
        setTimeout(() => {
          this.successfulChangedThumb = false
        }, 20000)
      }
    },
  },
  methods: {
    ...mapActions('video', [
      'updateCta',
      'getVideoConfig',
      'createProfile',
      'setProfileToVideos',
      ,
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO',
      'UPDATE_STATUS_ERROR',
      'UPDATE_VIDEO_CONFIG_CTAS',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    handleDownloadThumb(thumbUrl) {
      let filename = thumbUrl.split('/')
      filename = filename[filename.length - 1]
      fetch(thumbUrl)
        .then((res) => res.blob())
        .then((blob) => {
          download(blob, filename)
        })
    },
    async getVideoDefaultThumbs() {
      const ret = await videosApi.getThumbs(this.video.id)
      this.defaultThumbsInfo = ret.data.response
    },
    async handleUpdateDefaultThumb(defaultThumb) {
      this.loadingDeleteThumb = true
      if (!this.isBunnyThumbnail) {
        await this.handleDeleteThumbnail()
      }
      await playerApi.updateVideoCurrentThumb({
        video_id: this.video.id,
        thumb: defaultThumb,
      })
      this.defaultThumbnail = defaultThumb
      await this.getVideoConfig(this.videoId)
      this.loadingDeleteThumb = false
      this.$root.$emit('updateEmbedPreviewChange')
    },
    checkIfSelectedPauseThumbHasButton() {
      if (
        this.selectedPauseThumb.metadata?.button &&
        Object.keys(this.selectedPauseThumb.metadata?.button).length &&
        !this.selectedPauseThumb.metadata.thumb?.hasButton
      ) {
        this.selectedPauseThumb.metadata.thumb.hasButton = true
      }
    },
    isEmpty(obj) {
      return _.isEmpty(obj)
    },
    async handleLoadPauseThumbBtn(timeout) {
      if (!this.selectedPauseThumb.metadata.button) {
        this.selectedPauseThumb.metadata.button = _.cloneDeep(this.defaultBtn)
        this.selectedPauseThumb.metadata.button.startTime =
          this.formatSecondsToTime(this.selectedPauseThumb.startTime)
        this.selectedPauseThumb.metadata.button.endTime =
          this.formatSecondsToTime(this.selectedPauseThumb.endTime)
      }
      let imageURL = this.selectedPauseThumb.metadata.fileUrl
      if (imageURL.includes('blob:')) {
        const imageBase64 = await this.toBase64(this.newPauseThumbnail.file)
        imageURL = imageBase64
      }
      setTimeout(() => {
        this.$root.$emit(
          'editPauseThumbButton',
          this.selectedPauseThumb.metadata.button,
          imageURL
        )
      }, timeout)
    },
    async handleLoadPauseThumbNoBtn() {
      delete this.selectedPauseThumb.metadata.button
      const hasImageAttached = Boolean(this.selectedPauseThumb.metadata.fileUrl)

      if (hasImageAttached) {
        let imageURL = this.selectedPauseThumb.metadata.fileUrl
        if (imageURL.includes('blob:')) {
          const imageBase64 = await this.toBase64(this.newPauseThumbnail.file)
          imageURL = imageBase64
        }
        const cta = this.generateThumbCta(imageURL, 'pauseThumbnail')
        this.handlePostMessageCtas([cta])
      } else {
        this.handleEmptyIframeCtas()
      }
    },
    async handlePlayerPreviewPauseThumb(timeout = 300) {
      const hasValidThumbBtn = this.selectedPauseThumb.metadata.thumb.hasButton
      if (hasValidThumbBtn) {
        this.handleLoadPauseThumbBtn(timeout)
      } else {
        this.handleLoadPauseThumbNoBtn()
      }
    },
    handleOpenThumbLink() {
      window.open(this.getPauseThumbnailLink(), '_blank')
    },
    handleEmptyIframeCtas() {
      this.handlePostMessageCtas([])
    },
    handlePostMessageCtas(ctas) {
      if (this.video.hasOwnProperty('video_source')) {
        this.$root.$emit('setCtas', ctas)
      } else {
        const iframe = document.getElementById('panda-player').contentWindow
        iframe.postMessage(
          {
            type: 'ctas',
            parameter: {
              ctas,
            },
          },
          '*'
        )
      }
    },
    inRange(x, min, max) {
      return (x - min) * (x - max) <= 0
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
      })
    },
    showPreviewImg() {
      if (!this.newPauseThumbnail?.file) return
      const previewUrl = URL.createObjectURL(this.newPauseThumbnail.file)
      this.previewImg = previewUrl
      if (!this.pauseThumbnails.length && this.selectedPauseThumb) {
        this.selectedPauseThumb.metadata.fileUrl = previewUrl
        this.pauseThumbnails.push(this.selectedPauseThumb)
      } else {
        const defaultObj = this.getDefaultPauseThumbObj()
        defaultObj.metadata.fileUrl = previewUrl
        defaultObj.endTime = 5

        this.pauseThumbnails.splice(0, 0, defaultObj)
        this.selectedPauseThumb = this.pauseThumbnails[0]
        this.checkIfSelectedPauseThumbHasButton()
      }
      this.handlePlayerPreviewPauseThumb()
    },
    handlePublishPauseThumbnail() {
      if (this.selectedPauseThumb.metadata.thumb.hasButton) {
        this.$root.$emit('getPauseThumbButton')
      } else {
        this.handleUploadPauseThumbnail()
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    handleDiscardPauseThumb() {
      const pauseThumbsListIndex = this.pauseThumbnails.findIndex(
        (item) => item.id === this.selectedPauseThumb.id
      )
      if (pauseThumbsListIndex !== -1) {
        this.pauseThumbnails.splice(
          pauseThumbsListIndex,
          1,
          _.cloneDeep(this.originalSelectedPauseThumb)
        )
      }
      this.selectedPauseThumb = _.cloneDeep(this.originalSelectedPauseThumb)
      this.checkIfSelectedPauseThumbHasButton()
      this.handlePlayerPreviewPauseThumb(0)
      this.$root.$emit('updateEmbedPreviewChange')
    },
    onScroll() {
      this.scrollInvoked++
    },
    handleBackFromThumbForm() {
      this.previewImg = null
      this.isEditingThumb = false
    },
    resetForm() {
      this.defaultPauseThumbnail = ''
      this.editingThumbnail = {
        title: '',
        startTime: '00:00',
        endTime: '00:05',
        hasButton: false,
        type: 'pause',
      }
      this.previewImg = null
      this.isThumbUpdate = false
      this.newPauseThumbnail.file = null
    },
    handleEditThumb(thumbnailCta) {
      this.resetForm()
      this.isThumbUpdate = false
      if (thumbnailCta?.id) {
        if (thumbnailCta.metadata.thumb) {
          this.editingThumbnail = thumbnailCta.metadata.thumb
          this.editingThumbnail.startTime = thumbnailCta.startTime
          this.editingThumbnail.endTime = thumbnailCta.endTime
        }
        this.defaultPauseThumbnail = thumbnailCta.metadata.fileUrl
        this.beingEditedPauseThumb = thumbnailCta
        this.isThumbUpdate = true
      }
      this.isEditingThumb = true
      if (thumbnailCta?.metadata?.button) {
        setTimeout(() => {
          this.$root.$emit('editPauseThumbButton', thumbnailCta.metadata.button)
        }, 300)
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    handleChangeThumbType(type) {
      this.isEditingThumb = this.arrayCtas.length === 0
      this.editingThumbnail.type = type
    },
    async handleChangeThumbnail() {
      this.successfulDeleteThumb = false
      this.successfulChangedThumb = false
      this.clearErrors()
      const file = this.newThumbnail.file
      let fileExtension =
        file?.name?.substring(
          file?.name?.lastIndexOf('.') + 1,
          file?.name?.length
        ) || file?.name
      if (
        fileExtension !== 'jpg' &&
        fileExtension !== 'png' &&
        fileExtension !== 'jpeg' &&
        fileExtension !== 'gif'
      ) {
        this.errors.wrongThumbnailFile = true
        this.newThumbnail.file = null
        return
      }
      this.loading = true
      const data = new FormData()
      data.append('file', file)
      try {
        const ret = await videosApi.addThumbnailToVideo(this.videoId, data)
        await this.getVideoConfig(this.videoId)
        this.loading = false
        this.defaultThumbnail = `${process.env.VUE_APP_THUMB_API}/${this.video.pullzone_name}/${ret.data.response.thumb}`
        this.successfulChangedThumb = true
        this.$root.$emit('updateEmbedPreviewChange')
        return ret
      } catch (e) {
        if ((e.response && e.response.status === 413) || !e.response)
          this.errors.thumbAboveMaxSize = true
        this.loading = false
        this.errors.updateThumbnailVideo = true
      }
      this.loading = false
    },
    getPauseThumbUploadFormData() {
      if (this.selectedPauseThumb.metadata.hasOwnProperty('thumbId')) return
      const file = this.newPauseThumbnail.file
      if (file) {
        let fileExtension =
          file?.name?.substring(
            file?.name?.lastIndexOf('.') + 1,
            file?.name?.length
          ) || file?.name
        if (
          fileExtension !== 'jpg' &&
          fileExtension !== 'png' &&
          fileExtension !== 'jpeg' &&
          fileExtension !== 'gif'
        ) {
          this.errors.wrongThumbnailFile = true
          this.newPauseThumbnail.file = null
          return
        }
        let data = new FormData()
        data.append('file', file)
        this.$root.$emit('updateEmbedPreviewChange')
        return data
      }
      return
    },
    async handleUploadPauseThumbnail(createdBtnInfo) {
      if (this.selectedPauseThumb.metadata.thumbId) {
        this.isThumbUpdate = true
      } else {
        this.isThumbUpdate = false
      }
      this.successfulDeleteThumb = false
      this.successfulChangedThumb = false
      this.clearErrors()

      const uploadFormData = this.getPauseThumbUploadFormData()
      if (this.errors.wrongThumbnailFile) return
      this.loadingPauseThumbnail = true

      try {
        if (this.isThumbUpdate) {
          await this.handleDeletePauseThumbnail(
            this.selectedPauseThumb.id,
            this.selectedPauseThumb.metadata.thumbId,
            Boolean(uploadFormData),
            true
          )
        }

        let ret
        if (uploadFormData) {
          ret = await videosApi.addThumbnailToPauseVideo(
            this.videoId,
            uploadFormData
          )
        }
        let thumbFilename =
          ret?.data?.response?.pauseThumbnail[0] ||
          this.selectedPauseThumb.metadata.thumbId
        await this.handleUpdateConfigPauseThumbnail({
          ...createdBtnInfo,
          thumbFilename,
        })
        this.checkIfSelectedPauseThumbHasButton()
        this.handlePlayerPreviewPauseThumb()

        this.loadingPauseThumbnail = false
      } catch (e) {
        this.loadingPauseThumbnail = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    async handleUploadEndThumbnail() {
      this.successfulDeleteThumb = false
      this.successfulChangedThumb = false

      this.clearErrors()

      const { file } = this.newEndThumbnail

      let fileExtension =
        file?.name?.substring(
          file?.name?.lastIndexOf('.') + 1,
          file?.name?.length
        ) || file?.name

      if (!['jpg', 'png', 'jpeg', 'gif'].includes(fileExtension)) {
        this.errors.wrongThumbnailFile = true
        this.newEndThumbnail.file = null

        return
      }

      this.loadingEndThumbnail = true

      const data = new FormData()
      data.append('file', file)

      try {
        if (this.thumbnailHas('end')) {
          this.defaultEndThumbnail = null
          await this.handleDeleteEndThumbnail()
        }

        let ret = await videosApi.addThumbnailToEndVideo(this.videoId, data)
        let thumbName = ret.data.response.endThumbnail

        if (!this.thumbnailHas('end')) {
          this.UPDATE_VIDEO_CONFIG_CTAS(this.arrayCtas)

          this.UPDATE_STATUS_ERROR(false)

          this.loadPublishedCtas()
        }

        await this.handleUpdateConfigEndThumbnail(thumbName)
        this.handleSnackbar()
      } catch (e) {
        console.error(e)
        if ((e.response && e.response.status === 413) || !e.response)
          this.errors.thumbAboveMaxSize = true
        this.errors.updateThumbnailVideo = true
        this.loadingEndThumbnail = false
      }
      this.loadingEndThumbnail = false
      this.$root.$emit('updateEmbedPreviewChange')
    },
    getThumbsLink(thumb) {
      if (thumb.includes('/')) {
        thumb = thumb.split('/').slice(-1)
      }
      return `${THUMB_URL}/${this.$store.state.video.video?.pullzone_name}/${thumb}`
    },
    async handleUpdateConfigPauseThumbnail(formInfo) {
      const currentPauseThumbnailLink = this.getThumbsLink(
        formInfo.thumbFilename
      )
      try {
        const cta = this.generateThumbCta(
          currentPauseThumbnailLink,
          'pauseThumbnail',
          formInfo
        )
        const ret = await playerApi.addCta(this.videoId, cta)

        let pauseArray = ret.data.ctas.filter((item) => item.type === 'pause')
        let lastItem = pauseArray[pauseArray.length - 1]
        pauseArray.pop()
        pauseArray.unshift(lastItem)
        const draftPauseThubms = this.pauseThumbnails.filter(
          (item) =>
            !item.metadata.hasOwnProperty('thumbId') &&
            item.id !== this.selectedPauseThumb.id
        )
        this.pauseThumbnails = [...draftPauseThubms, ...pauseArray]
        this.selectedPauseThumb =
          this.pauseThumbnails.filter(({ id }) => id === cta.id)[0] ??
          this.getDefaultPauseThumbObj()
        this.originalPauseThumbnailsList = _.cloneDeep(pauseArray)
        this.arrayCtas = ret.data.ctas
        this.handleBackFromThumbForm()
        await this.getVideoConfig(this.videoId)
        this.UPDATE_VIDEO_CONFIG_CTAS(this.arrayCtas)
        if (!this.internalError) {
          this.withoutChanges = true
          this.loadingPauseThumbnail = false
          this.defaultPauseThumbnail =
            this.$store.state.video.video?.pauseThumbnail
          this.successfulChangedThumb = true
          this.loadPublishedCtas()
          this.loadingSkipButton = false
          this.handleSnackbar()
        }
      } catch (e) {
        console.error(e)
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    async handleUpdateConfigEndThumbnail(thumbName) {
      const endThumbnailLink = this.getThumbsLink(thumbName)

      try {
        const cta = await this.generateThumbCta(
          endThumbnailLink,
          'endThumbnail',
          null
        )
        const ret = await playerApi.addCta(this.videoId, cta)

        this.arrayCtas = ret.data.ctas
        this.UPDATE_VIDEO_CONFIG_CTAS(this.arrayCtas)

        if (this.internalError) {
          this.withoutChanges = true
          this.loadingEndThumbnail = false
          this.successfulChangedThumb = true

          this.loadingSkipButton = false

          this.handleSnackbar()
        }

        this.loadPublishedCtas()

        this.defaultEndThumbnail = endThumbnailLink
      } catch (e) {
        console.error(e)
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    generateThumbCta(thumbnail, type, formInfo) {
      let codeThumbnail
      let ctaScript
      if (type === 'pauseThumbnail') {
        if (this.selectedPauseThumb.metadata.thumb.hasButton) {
          ctaScript = formInfo.script
          const base64Index = ctaScript.indexOf(
            `background-image: url('data:image`
          )
          if (base64Index >= 0) {
            const base64Content = ctaScript
              .split(`background-image: url('`)[1]
              .split(`')`)[0]
            ctaScript = ctaScript.replace(base64Content, thumbnail)
          }
        } else {
          ctaScript =
            `<img src='${thumbnail}' style='object-fit:cover; width:100%; height:100%;'><script>document.querySelector('img').onclick=function() { window.parent.postMessage({type: 'cta_play',}, '*') }</scr` +
            `ipt>`
        }
      }
      if (type === 'pauseThumbnail') {
        codeThumbnail = thumbnail
          ? {
              id: this.generateUuid(),
              script: ctaScript,
              type: 'pause',
              metadata: {
                thumb: {
                  title: this.selectedPauseThumb.metadata.thumb.title,
                  startTime: this.selectedPauseThumb.startTime,
                  endTime: this.selectedPauseThumb.endTime,
                  hasButton:
                    this.selectedPauseThumb.metadata.thumb.hasButton || false,
                  type: 'pause',
                  lastAllVideo:
                    this.selectedPauseThumb.metadata.thumb.lastAllVideo ||
                    false,
                  lastTillEnd:
                    this.selectedPauseThumb.metadata.thumb.lastTillEnd || false,
                },
                fileUrl: thumbnail,
                thumbId: formInfo?.thumbFilename ?? this.generateUuid(),
              },
              startTime: this.selectedPauseThumb.startTime,
              endTime: this.selectedPauseThumb.endTime,
            }
          : false
        if (formInfo?.buttonMetadata && thumbnail) {
          codeThumbnail.metadata.button = formInfo.buttonMetadata
        }
      }
      if (type === 'endThumbnail') {
        codeThumbnail = {
          id: this.generateUuid(),
          script:
            `<img src='${thumbnail}' style='object-fit:cover; width:100%; height:100%;'><script>document.querySelector('img').onclick=function() { window.parent.postMessage({type: 'cta_play',}, '*') }</scr` +
            `ipt>`,
          type: 'end',
          metadata: {
            fileUrl: thumbnail,
          },
        }
      }
      return codeThumbnail
    },
    loadPublishedCtas() {
      const arrayCtas = this.$store.state.video.video.config?.ctas
      if (!arrayCtas) {
        return
      }
      if (this.video.hasOwnProperty('video_source')) {
        this.$root.$emit('setCtas', arrayCtas)
      } else {
        const iframe = document.getElementById('panda-player').contentWindow
        iframe.postMessage(
          {
            type: 'ctas',
            parameter: {
              ctas: arrayCtas,
            },
          },
          '*'
        )
        return
      }
    },
    getDefaultPauseThumbObj() {
      return {
        startTime: 0,
        endTime: this.$store.state.video.video?.length,
        type: 'pause',
        metadata: {
          thumb: {
            title: '',
            hasButton: false,
          },
        },
        id: uuidv4(), //temp id
      }
    },
    async handleDeleteThumbnail() {
      this.successfulDeleteThumb = false
      this.successfulChangedThumb = false
      this.clearErrors()
      this.loadingDeleteThumb = true
      try {
        const ret = await videosApi.deleteThumbnailVideo(this.videoId)
        await this.getVideoConfig(this.videoId)
        this.loadingDeleteThumb = false
        this.defaultThumbnail = this.$store.state.video.video.thumbnail
        this.successfulDeleteThumb = true
        this.$root.$emit('updateEmbedPreviewChange')
        return ret
      } catch (error) {
        this.loadingDeleteThumb = false
        this.errors.updateThumbnailVideo = true
        this.$root.$emit('updateEmbedPreviewChange')
      }
    },
    async handleRemoveThumb(pauseCta, index = 0) {
      if (!pauseCta.metadata.thumbId) {
        this.pauseThumbnails.splice(index, 1)
        return
      }
      await this.handleDeletePauseThumbnail(
        pauseCta.id,
        pauseCta.metadata.thumbId,
        true
      )

      this.pauseThumbnails = this.pauseThumbnails.filter(
        (item) => item.id !== pauseCta.id
      )
      this.originalPauseThumbnailsList = _.cloneDeep(this.pauseThumbnails)

      this.selectedPauseThumb =
        this.pauseThumbnails[0] || this.getDefaultPauseThumbObj()
      this.checkIfSelectedPauseThumbHasButton()
      this.handlePlayerPreviewPauseThumb()
      this.isEditingThumb = this.arrayCtas.length === 0
      if (!this.pauseThumbnails.length) {
        this.showEditPauseThumbs = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    async handleDeletePauseThumbnail(
      ctaId,
      thumbFilename,
      shouldRemoveThumbFile,
      updateBehavior = false
    ) {
      this.successfulDeletePauseThumb = false
      this.successfulChangedPauseThumb = false
      this.clearErrors()
      this.loadingDeletePauseThumb = true
      try {
        let ret
        if (shouldRemoveThumbFile) {
          this.beingDeletePauseThumbId = ctaId
          ret = await videosApi.deleteThumbnailPauseVideo(
            this.videoId,
            thumbFilename
          )
        }
        const res = await playerApi.deleteCta(this.videoId, ctaId)
        await this.getVideoConfig(this.videoId)
        this.arrayCtas = res.data.ctas
        this.UPDATE_VIDEO_CONFIG_CTAS(this.arrayCtas)
        if (!updateBehavior) {
          this.pauseThumbnails = this.pauseThumbnails.filter(
            (item) => item.id !== ctaId
          )
        }
        this.originalPauseThumbnailsList = _.cloneDeep(this.pauseThumbnails)
        this.loadPublishedCtas()
        this.loadingDeletePauseThumb = false
        this.defaultPauseThumbnail = null
        this.havePauseThumb = false
        this.beingDeletePauseThumbId = ''
        return ret
      } catch (error) {
        console.error(error)
        this.loadingDeletePauseThumb = false
        this.havePauseThumb = true
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    async handleDeleteEndThumbnail(removeButton = null) {
      this.successfulDeleteEndThumb = false
      this.successfulChangedEndThumb = false

      this.clearErrors()

      this.loadingDeleteEndThumb = true

      try {
        const found = _.filter(this.arrayCtas, { type: 'end' })
        let res

        for (let i = 0; i < found.length; i++) {
          try {
            await videosApi.deleteThumbnailEndVideo(this.videoId)
          } catch {}
        }

        for (const { id } of found) {
          res = await playerApi.deleteCta(this.videoId, id)
        }

        this.defaultEndThumbnail = null
        await this.getVideoConfig(this.videoId)

        this.arrayCtas = res.data.ctas
        this.UPDATE_VIDEO_CONFIG_CTAS(this.arrayCtas)

        this.UPDATE_STATUS_ERROR(false)

        this.loadPublishedCtas()
        this.loadingDeleteEndThumb = false
        this.haveEndThumb = false

        if (removeButton === 'removeButton') {
          this.successfulDeleteThumb = true
        }
      } catch (error) {
        this.loadingDeleteEndThumb = false
        this.haveEndThumb = true
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    thumbnailHas(value) {
      const arrayCtas = this.$store.state.video.video.config?.ctas

      if (!arrayCtas) return false

      return arrayCtas.some((item) => item.type === value)
    },
    generateUuid() {
      return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (
          c ^
          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16)
      )
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
    handleSnackbar(item) {
      if (!item) {
        item = this.$i18next.t('message.pages.video.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
    formatSecondsToTime(seconds) {
      let res = new Date(seconds * 1000).toISOString()
      if (this.timeVideo < 3600) {
        return res.substring(14, 19)
      }
      return res.substring(11, 19)
    },
    formatTimeToSeconds(time) {
      let res = time.split(':').reduce((acc, time) => 60 * acc + +time)
      return res
    },
    formatVideoLength() {
      let videoLength = this.$store.state.video.video?.length
      this.timeVideo = videoLength
    },
    handleStartTimeEvent(val) {
      this.selectedPauseThumb.metadata.thumb.lastAllVideo = false
      this.selectedPauseThumb.startTime = this.formatTimeToSeconds(val)
      this.handlePlayerPreviewPauseThumb()
    },
    handleEndTimeEvent(val) {
      this.selectedPauseThumb.metadata.thumb.lastTillEnd = false
      this.selectedPauseThumb.endTime = this.formatTimeToSeconds(val)
      this.handlePlayerPreviewPauseThumb()
    },
    handleLastAllVideo() {
      if (this.selectedPauseThumb.metadata.thumb.lastAllVideo) {
        this.selectedPauseThumb.startTime = this.formatTimeToSeconds(
          this.getDefaultTimes().start
        )
        this.selectedPauseThumb.endTime = this.timeVideo
        this.selectedPauseThumb.metadata.thumb.lastTillEnd = false
      }
      this.renderTick += 1
      this.handlePlayerPreviewPauseThumb()
    },
    handleLastTillEnd() {
      if (this.selectedPauseThumb.metadata.thumb.lastTillEnd) {
        this.selectedPauseThumb.endTime = this.timeVideo
        this.selectedPauseThumb.metadata.thumb.lastAllVideo = false
      }
      this.renderTick += 1
      this.handlePlayerPreviewPauseThumb()
    },
    getDefaultTimes() {
      if (this.timeVideo && this.timeVideo >= 3600) {
        return {
          start: '00:00:00',
          end: '00:05:00',
        }
      }
      return {
        start: '00:00',
        end: '00:05',
      }
    },
    getEndThumbUrl() {
      if (this.thumbnailHas('end')) {
        const arrayCtas = this.$store.state.video.video.config?.ctas
        const endThumbnail = arrayCtas.find((item) => item.type === 'end')
        const htmlString = endThumbnail.script
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = htmlString
        const imgElement = tempDiv.querySelector('img')
        const thumbUrl = imgElement.getAttribute('src')
        return thumbUrl
      }
      return false
    },
  },
}
</script>

<style lang="scss">
.card-thumbnail {
  border: 2px dashed #d0d1d9 !important;
  box-shadow: none !important;
  border-radius: 4px;
  padding: 12px 16px;
}

.card-current-thumb {
  box-shadow: none !important;
}

.card-thumbnail .v-icon.v-icon::after {
  background-color: transparent !important;
}

.card-thumbnail .v-icon {
  width: 150px !important;
  height: 92px !important;
  z-index: 2;
}

.card-thumbnail .v-icon:before {
  margin-top: -25px !important;
}

.card-thumbnail .v-input__icon {
  width: 150px !important;
  height: 100%;
  z-index: 2;
  opacity: 0;
}

.card-thumbnail .v-input {
  padding: 0 !important;
}

.card-thumbnail .v-input__prepend-outer {
  margin-right: 0 !important;
}

.container__thumb-list {
  overflow-y: hidden;
  overflow-x: auto;
  padding: 8px 0px !important;
}

.container__thumb-list::-webkit-scrollbar {
  height: 2px;
}

.container__thumb-list::-webkit-scrollbar-track {
  background: #edeef1;
}

.container__thumb-list::-webkit-scrollbar-thumb {
  background: var(--v-brand-base);
  border-radius: 8px;
}
</style>
